import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";
import { LANG_PREF_KEY, splitLangPath, withLang } from "../i18n/urls";

const LanguageSwitcher = ({ variant = "header" }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeCode = (i18n.resolvedLanguage || i18n.language || "it").split("-")[0];
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === activeCode) || SUPPORTED_LANGUAGES[0];

  const changeLanguage = (code) => {
    try {
      localStorage.setItem(LANG_PREF_KEY, code);
    } catch (e) {}
    i18n.changeLanguage(code);
    setOpen(false);
    const { path } = splitLangPath(pathname);
    navigate(`${withLang(path, code)}${search}${hash}`);
  };

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen(!open)}
        data-testid="language-switcher-btn"
        className={`flex items-center gap-2 ${
          variant === "header"
            ? "text-white/80 hover:text-arabell-gold text-[10px] tracking-[0.25em] uppercase"
            : "text-white/70 hover:text-white text-xs"
        } transition`}
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{current.flag} {current.code.toUpperCase()}</span>
        <span className="sm:hidden">{current.flag}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 bg-black border border-arabell-gold/30 py-2 min-w-[180px] z-[100] shadow-2xl">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={`w-full text-left px-4 py-2 text-xs hover:bg-arabell-gold/10 flex items-center justify-between transition ${
                lang.code === activeCode ? "text-arabell-gold" : "text-white/80"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-base">{lang.flag}</span>
                <span>{lang.name}</span>
              </span>
              {lang.code === activeCode && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
